import {
  Badge,
  type BadgeProps,
  HStack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type AccountMoveFragment,
  BankAccountMoveStatusEnum,
  BankAccountMovesDocument,
  type BankAccountMovesQuery,
  type BankAccountMovesQueryVariables,
  BankAccountsForPaymentsDocument,
  type BankAccountsForPaymentsQuery,
  type BankAccountsForPaymentsQueryVariables,
  BankAccountWhereColumn,
  BankDocumentTypeEnum,
  ExcludeAccountMoveDocument,
  type ExcludeAccountMoveMutation,
  type ExcludeAccountMoveMutationVariables,
  SqlOperator,
} from "@src/__generated__/graphql";
import { type BodyColumnDef, type IOption, Link } from "@src/components/ui-kit";
import { TruncatedTextWithTooltip } from "@src/components/ui-kit/DataTable/Body/components";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { type AppStore, appStore } from "@src/stores/AppStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { currency, denominate, formatDate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { upperFirst } from "lodash";
import { action, makeObservable, observable } from "mobx";
import type { LinkProps } from "next/link";
import type { AddManualMoveModalStore } from "../add-manual-move-modal/store";
import type { BankMatchPaymentModalStore } from "../match-payment-modal/store";

export class BankStore {
  readonly tableKey = "bank-listing-table";

  fetcher = new FetchHelper<
    BankAccountMovesQuery,
    BankAccountMovesQueryVariables
  >(BankAccountMovesDocument);
  bankAccountsFetcher = new FetchHelper<
    BankAccountsForPaymentsQuery,
    BankAccountsForPaymentsQueryVariables
  >(BankAccountsForPaymentsDocument);
  excludeMutator = new MutationHelper<
    ExcludeAccountMoveMutation,
    ExcludeAccountMoveMutationVariables
  >(ExcludeAccountMoveDocument);
  @observable.ref loadingRowId: string | undefined;

  @observable.ref searchTerm = "";
  where = new Filters<BankAccountWhereColumn>(
    [
      new Filter({
        title: t`Bank Account`,
        column: BankAccountWhereColumn.BankAccountId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Status`,
        column: BankAccountWhereColumn.Status,
        operator: SqlOperator.In,
        options: BankStore.getStatusFilterOptions(),
      }),
      new Filter({
        title: t`Amount from`,
        column: BankAccountWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        options: [],
        mutateToUrl: BankStore.mutateAmountFilter,
      }),
      new Filter({
        title: t`Amount to`,
        column: BankAccountWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        options: [],
        mutateToUrl: BankStore.mutateAmountFilter,
      }),
      new Filter({
        title: t`Transaction type`,
        column: BankAccountWhereColumn.AmountType,
        operator: SqlOperator.Eq,
        options: [
          {
            label: t`Credit`,
            value: "credit",
          },
          {
            label: t`Debit`,
            value: "debit",
          },
        ],
      }),
    ],
    {
      onChange: () => this.pagination.resetPage(),
      tableKey: this.tableKey,
    },
  );
  pagination = new PaginationState(`${this.tableKey}-pagination`, {
    onChangePagination: () => this.fetchData(),
    perPage: 50,
    perPageOptions: [50, 100, 150],
  });

  @observable.ref bankAccountOptions: IOption[] = [];
  @observable tableData: AccountMoveFragment[] = [];

  constructor(
    private appStore: AppStore,
    public matchPaymentModalStore: BankMatchPaymentModalStore,
    public addManualMoveModalStore: AddManualMoveModalStore,
  ) {
    makeObservable(this);
    this.fetchData();
    this.fetchAccountOptions();
  }

  static getStatusFilterOptions(): IOption[] {
    return [
      {
        value: BankAccountMoveStatusEnum.Paired,
        label: t`Paired`,
      },
      {
        value: BankAccountMoveStatusEnum.NotPaired,
        label: t`Not Paired`,
      },
      {
        value: BankAccountMoveStatusEnum.ManualPaired,
        label: t`Manual Paired`,
      },
      {
        value: BankAccountMoveStatusEnum.Excluded,
        label: t`Excluded`,
      },
    ];
  }

  static mutateAmountFilter(value: string[]) {
    if (!value || value.length === 0) return [];
    if (value[0] === "") return [];
    return [
      denominate(
        Number(value[0]),
        appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ).toString(),
    ];
  }

  private static getStatusBadgeColorScheme(
    status: BankAccountMoveStatusEnum,
  ): BadgeProps["colorScheme"] {
    switch (status) {
      case BankAccountMoveStatusEnum.Paired:
        return "green";
      case BankAccountMoveStatusEnum.NotPaired:
        return "red";
      case BankAccountMoveStatusEnum.ManualPaired:
        return "orange";
      default:
        return "gray";
    }
  }

  private static getUrlForDocument(
    document: NonNullable<AccountMoveFragment["documents"]>[0],
  ): LinkProps["href"] {
    return {
      pathname:
        document.document_type === BankDocumentTypeEnum.Expense
          ? "/expenses/edit"
          : document.document_type === BankDocumentTypeEnum.OutgoingInvoice
            ? "/invoices/outgoing-invoices/detail"
            : "/invoices/proforma-invoices/detail",
      query: { id: document.document_id },
    };
  }

  static getColumnsWithoutActions(hideUnnecessaryColumns = false) {
    const cols: BodyColumnDef<AccountMoveFragment, unknown>[] = [
      {
        id: "status",
        meta: { label: t`Status` },
        header: () => t`Status`,
        cell: ({ row }) => (
          <Badge
            colorScheme={BankStore.getStatusBadgeColorScheme(
              row.original.status,
            )}
          >
            {row.original.status.replaceAll("_", " ")}
          </Badge>
        ),
      },
      {
        id: "date",
        meta: { label: t`Date` },
        header: () => t`Date`,
        cell: ({ row }) =>
          row.original.date ? (
            <Text>{formatDate(new Date(row.original.date))}</Text>
          ) : null,
      },
      {
        id: "reference",
        meta: { label: t`Reference` },
        header: () => t`Reference`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.reference} />
        ),
      },
    ];

    if (
      appStore.workspaceStore.settings?.country.code === "SK" ||
      appStore.workspaceStore.settings?.country.code === "CZ"
    ) {
      cols.push(
        {
          id: "variable-symbol",
          meta: { label: t`Variable symbol` },
          header: () => t`Variable symbol`,
          cell: ({ row }) => row.original.vs,
        },
        {
          id: "specific-symbol",
          meta: { label: t`Specific symbol` },
          header: () => t`Specific symbol`,
          cell: ({ row }) => row.original.ss,
        },
        {
          id: "constant-symbol",
          meta: { label: t`Constant symbol` },
          header: () => t`Constant symbol`,
          cell: ({ row }) => row.original.ks,
        },
      );
    }

    cols.push(
      {
        id: "amount",
        meta: { label: t`Amount` },
        header: () => t`Amount`,
        cell: ({ row }) =>
          currency.formatByCurrency(row.original.amount, row.original.currency),
      },
      {
        id: "balance",
        meta: { label: t`Balance` },
        header: () => t`Balance`,
        cell: ({ row }) => (
          <Text fontWeight="bold">
            <strong>
              {currency.formatByCurrency(
                row.original.balance,
                row.original.currency,
              )}
            </strong>
          </Text>
        ),
      },
      {
        id: "iban",
        meta: { label: t`IBAN` },
        header: () => t`IBAN`,
        cell: ({ row }) => (
          <Tooltip label={row.original.iban} shouldWrapChildren>
            <Text maxW="100px" isTruncated>
              {row.original.iban}
            </Text>
          </Tooltip>
        ),
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: () => t`Note`,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.note} />
        ),
      },
      {
        id: "type",
        meta: { label: t`Match` },
        header: () => t`Match`,
        cell: ({ row }) => (
          <Badge>{row.original.type.replaceAll("-", "")}</Badge>
        ),
      },
      {
        id: "paired-documents",
        meta: { label: t`Paired documents` },
        header: () => t`Paired documents`,
        cell: ({ row }) =>
          row.original.documents ? (
            <HStack spacing="0">
              {row.original.documents.map((document, index) => (
                <Tooltip
                  key={`paired-document-${document.id}`}
                  label={upperFirst(
                    document.document_type.replaceAll("_", " ").toLowerCase(),
                  )}
                  placement="top"
                >
                  <Text>
                    <Link href={BankStore.getUrlForDocument(document)}>
                      {document.document_no}
                    </Link>
                    {index < (row.original.documents?.length ?? 1) - 1 && (
                      <>,&nbsp;</>
                    )}
                  </Text>
                </Tooltip>
              ))}
            </HStack>
          ) : null,
      },
    );

    return hideUnnecessaryColumns
      ? cols.filter(
          (column) => column.id !== "type" && column.id !== "paired-documents",
        )
      : cols;
  }

  @action.bound setSearch(newValue: string): void {
    this.searchTerm = newValue;
    this.pagination.resetPage();
  }

  @action.bound async fetchAccountOptions() {
    const [data, error] = await this.bankAccountsFetcher.fetch();

    if (error) return;

    this.bankAccountOptions = mapToOptions.paymentBankAccounts(
      data.bankAccounts ?? [],
    );
    this.where.filtersByColumn
      .get(BankAccountWhereColumn.BankAccountId)
      ?.setOptions(this.bankAccountOptions);
    this.addManualMoveModalStore.setBankAccountOptions(this.bankAccountOptions);
  }

  @action.bound async fetchData() {
    const [data, error] = await this.fetcher.fetch(
      {
        filters: {
          where: this.where.asWhereParam,
          search: this.searchTerm,
        },
        page: this.pagination.asParams.page,
        first: this.pagination.asParams.first,
      },
      undefined,
    );

    if (error || !data.bankAccountMoves) return;

    this.tableData = data.bankAccountMoves.data;
    this.pagination.setFromPaginatorInfo(data.bankAccountMoves.paginatorInfo);
  }

  @action.bound async excludeAccountMove(accountMoveId: string) {
    this.loadingRowId = accountMoveId;
    const [data, error] = await this.excludeMutator.mutate({
      accountMoveId: accountMoveId,
    });

    this.loadingRowId = undefined;
    if (error || !data.excludeBankAccountMove) return;
    this.updateRow(data.excludeBankAccountMove);
  }

  @action.bound
  updateRow(updatedRow: AccountMoveFragment) {
    let rowIndex: number | undefined;

    const rowCount = this.tableData.length;
    for (let i = 0; i < rowCount; i++) {
      const row = this.tableData[i];
      if (row.id !== updatedRow.id) continue;
      rowIndex = i;
      break;
    }

    if (rowIndex === undefined) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Couldn't find row to be updated, please try again.`,
      });
      captureEvent({
        message: "FE: Couldn't find row to be updated.",
      });
      return;
    }

    this.tableData = this.tableData.map((row) =>
      row.id === updatedRow.id ? updatedRow : row,
    );
  }
}
